import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import ZipRoutingProfileService from "app/data/zip-routing-profile/zipRoutingProfileService";
import {
  ZipRoutingProfileActivityLogFilters,
  ZipRoutingProfileFormModel,
  ZipRoutingProfileListParams,
} from "app/data/zip-routing-profile/models";

const service = ZipRoutingProfileService.getInstance();

const invalidateRouteGuideActivityLogs = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ZIP_ROUTING_PROFILE_ACTIVITY_LOG] });
};

export const useZipRoutingProfileList = (filters: Omit<ZipRoutingProfileListParams, "page">) => {
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.ZIP_ROUTING_PROFILE_LIST,
      filters.search,
      filters.state,
      filters.terminalNumber,
      filters.direct,
      filters.size,
    ],
    queryFn: ({ pageParam = 0 }) =>
      service.getList({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.number ?? 0;
      const totalPages = lastPage?.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    select: (data) => {
      const allItems = data.pages.flatMap((p) => p.content ?? []);
      return { ...data, allItems };
    },
    keepPreviousData: true,
  });
};

export const useZipRoutingProfileDetails = (id?: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ZIP_ROUTING_PROFILE_DETAIL, id],
    queryFn: () => service.getById(id!),
    enabled: !!id,
  });
};

export const useZipRoutingProfileActivityLogList = (
  filters: ZipRoutingProfileActivityLogFilters,
  enabled: boolean = true
) => {
  const { search, action, dateFrom, dateTo } = filters;

  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.ZIP_ROUTING_PROFILE_ACTIVITY_LOG,
      search,
      action,
      dateFrom,
      dateTo,
    ],
    queryFn: ({ pageParam = 0 }) =>
      service.getActivityLogList(pageParam, {
        search,
        action,
        dateFrom,
        dateTo,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage?.last) return undefined;
      const currentPage = lastPage?.pageable?.pageNumber ?? lastPage?.number ?? 0;
      return currentPage + 1;
    },
    select: (data) => {
      const allItems = data.pages.flatMap((p) => p?.content || []);
      return { ...data, allItems };
    },
    enabled,
    staleTime: 1000 * 60,
  });
};

export const useCreateZipRoutingProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ZipRoutingProfileFormModel) => service.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ZIP_ROUTING_PROFILE_LIST] });
      invalidateRouteGuideActivityLogs(queryClient);
    },
  });
};

export const useUpdateZipRoutingProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ZipRoutingProfileFormModel }) =>
      service.update(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ZIP_ROUTING_PROFILE_LIST] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ZIP_ROUTING_PROFILE_DETAIL, variables.id],
      });
      invalidateRouteGuideActivityLogs(queryClient);
    },
  });
};
